<template>
  <div>
    <div class="grid grid-cols-2 gap-x-1 min-h-20">
      <div
        class="h-full rounded-t-xl px-[15px] py-[9px]"
        :class="{
          'bg-gray-300/30': !isRegisteredTabActive,
          'bg-gray-400': isRegisteredTabActive,
        }"
        @click="setActiveTab('registered')"
      >
        <div
          class="font-secondary font-medium text-[12px] leading-[18px] px-[5px] py-px rounded-md w-fit mb-[3px]"
          :class="{
            'opacity-60 bg-gray-200 text-gray-800': !isRegisteredTabActive,
            'opacity-full bg-gray-500 text-white': isRegisteredTabActive,
          }"
        >
          Registered work
        </div>

        <div
          class="font-secondary text-[13px] leading-[18px]"
          :class="{
            'opacity-60 text-gray-800': !isRegisteredTabActive,
            'opacity-full text-white': isRegisteredTabActive,
          }"
        >
          {{ match.copyright.location }}
        </div>
      </div>

      <div
        class="h-full rounded-t-xl px-[15px] py-[9px] flex flex-col"
        :class="backgroundClasses"
        @click="setActiveTab('infringing')"
      >
        <div
          class="font-secondary font-medium text-[12px] leading-[18px] px-[5px] py-px text-white rounded-md w-fit mb-[3px]"
          :class="pillClasses"
        >
          <span class="tablet-h:hidden">Match</span>
          <span class="hidden tablet-h:block">Potential infringement</span>
          {{ number }}
        </div>

        <div class="font-secondary text-[13px] leading-[18px] text-white">
          {{ match.infringingFile.location }}
        </div>

        <div class="flex items-center gap-x-3 mt-auto">
          <div
            class="font-secondary font-medium text-[12px] leading-[18px] text-white whitespace-nowrap"
          >
            {{ match.infringingFile.score }}% match
          </div>

          <div class="w-[70px] h-2 bg-white rounded-md p-px">
            <div
              class="h-full rounded-md"
              :class="backgroundClasses"
              :style="{
                width: `${Number(match.infringingFile.score)}%`,
              }"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <div class="border-2 rounded-b-xl p-6" :class="classes">
      <div v-if="isRegisteredTabActive" class="mb-[25px]">
        <div
          class="font-secondary text-[12px] leading-[16px] text-gray-500 mb-0.5"
        >
          Registration
        </div>

        <div class="flex gap-x-1.5 mb-[25px]">
          <ThumbnailIcon
            class="w-5 h-5 rounded-xs bg-gray-200/25 p-px"
            :icon-size="16"
            :image-url="copyrightFile.asset?.previewImageUrl"
            :type="copyrightFile.type"
          />

          <div>
            <div
              class="font-secondary font-medium text-[13px] leading-[20px] text-gray-800 mb-[3px]"
            >
              {{ copyrightFile.name }}
            </div>

            <div
              v-if="showOriginalFilename"
              class="font-secondary text-[12px] leading-[18px] text-gray-800 mb-[3px]"
            >
              Filename:
              {{ copyrightFile.metadata.originalFilename }}
            </div>

            <RouterLink
              :to="`/file/${copyrightFile.copyright.id}`"
              class="font-primary text-[12px] leading-[18px] text-link"
            >
              View registration
            </RouterLink>
          </div>
        </div>
      </div>

      <div v-if="isInfringingTabActive" class="mb-[25px]">
        <div
          class="font-secondary text-[12px] leading-[16px] text-gray-500 mb-0.5"
        >
          Potential infringing work
        </div>

        <div class="flex items-center gap-x-1.5">
          <ThumbnailIcon
            class="w-5 h-5 rounded-xs bg-gray-200/25 p-px"
            :icon-size="16"
            :image-url="infringingFile.asset.previewImageUrl"
            :type="infringingFile.type"
          />
          <div class="font-secondary text-[12px] leading-[18px] text-gray-800">
            {{ infringingFile.name }}
          </div>
        </div>
      </div>

      <template v-if="isInfringingTabActive">
        <div class="mb-[25px]">
          <div
            class="font-secondary text-[12px] leading-[16px] text-gray-500 mb-1.5"
          >
            Infringement findings
          </div>

          <div class="font-secondary text-[14px] leaidng-[20px]">
            {{ match.infringingFile.summary }}
          </div>
        </div>

        <div class="w-full h-px my-[25px] bg-gray-200/50"></div>
      </template>

      <component
        :key="activeTab"
        :is="component"
        :match="matchData"
        :number="number"
        :side="activeTab"
        :allow-playback="canPlayMatches"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  type FragmentType,
  useFragment,
} from '@/shared/graphql/router/fragment-masking'
import {
  MatchCategoryType,
  ScoreLevel,
  type File,
  type InfringingFile,
} from '@/shared/graphql/router/graphql'
import { computed, ref, type Component } from 'vue'
import AudioMatchGroup from './AudioMatchGroup.vue'
import ImageMatch from './ImageMatch.vue'
import { MatchBreakdownFragment } from './Match.vue'
import TextMatchGroup from './TextMatchGroup.vue'
import TranscriptMatch from './TranscriptMatch.vue'
import VideoMatch from './VideoMatch.vue'
import { ThumbnailIcon } from '@dacr/ui'

const props = defineProps<{
  type: MatchCategoryType
  match: FragmentType<typeof MatchBreakdownFragment>
  number: number
  allowPlayback?: boolean
}>()

const match = useFragment(MatchBreakdownFragment, props.match)

const canPlayMatches = computed(() => props.allowPlayback !== false)

const activeTab = ref<'registered' | 'infringing'>('infringing')

const isRegisteredTabActive = computed(() => activeTab.value === 'registered')
const isInfringingTabActive = computed(() => activeTab.value === 'infringing')

const copyrightFile = computed(() => match.copyright.file as File)
const infringingFile = computed(
  () => match.infringingFile.file as InfringingFile
)

const showOriginalFilename = computed(
  () =>
    copyrightFile.value.name !== copyrightFile.value.metadata.originalFilename
)

const classes = computed(() => ({
  'border-gray-400': isRegisteredTabActive.value,
  ...(isInfringingTabActive.value && {
    'border-red-500': match.infringingFile.level === ScoreLevel.High,
    'border-yellow-500': match.infringingFile.level === ScoreLevel.Medium,
    'border-green-500': match.infringingFile.level === ScoreLevel.Low,
  }),
}))

const setActiveTab = (tab: 'registered' | 'infringing') => {
  activeTab.value = tab
}

const component = computed<Component>(() => {
  if (props.type === MatchCategoryType.Audio) {
    return AudioMatchGroup
  }

  if (props.type === MatchCategoryType.Text) {
    return TextMatchGroup
  }

  if (props.type === MatchCategoryType.Transcript) {
    return TranscriptMatch
  }

  if (props.type === MatchCategoryType.Video) {
    return VideoMatch
  }

  return ImageMatch
})

const pillClasses = computed(() => ({
  'bg-red-600': match.infringingFile.level === ScoreLevel.High,
  'bg-yellow-600': match.infringingFile.level === ScoreLevel.Medium,
  'bg-green-600': match.infringingFile.level === ScoreLevel.Low,
}))

const backgroundClasses = computed(() => ({
  'opacity-40': isRegisteredTabActive.value,
  'bg-red-500': match.infringingFile.level === ScoreLevel.High,
  'bg-yellow-500': match.infringingFile.level === ScoreLevel.Medium,
  'bg-green-500': match.infringingFile.level === ScoreLevel.Low,
}))

const matchData = computed(() => {
  return activeTab.value === 'registered'
    ? match.copyright
    : match.infringingFile
})
</script>
